import type { ExerciseAnswer, ExerciseForm } from 'api/rest/exercises/exercises.types';

export type ExerciseSelectionMode =
  | 'single'
  | 'trueFalse'
  | 'multiSelect'
  | 'groupedSingleSelect'
  | 'groupedMultiSelect';

/** Controls whether GROUPED_MULTI_TEST allows multiple answers within the same group. */
export type GroupedMultiTestOptions = {
  allowMultiplePerGroup?: boolean;
};

export const DEFAULT_GROUPED_MULTI_TEST_OPTIONS: Required<GroupedMultiTestOptions> = {
  allowMultiplePerGroup: false,
};

/** Resolves GROUPED_MULTI_TEST interaction options. Extend when backend exposes per-exercise config. */
export const resolveGroupedMultiTestOptions = (): Required<GroupedMultiTestOptions> =>
  DEFAULT_GROUPED_MULTI_TEST_OPTIONS;

/** Maps exercise form to the On Go answer selection interaction. */
export const resolveExerciseSelectionMode = (
  form: ExerciseForm | undefined,
  groupedMultiTestOptions: GroupedMultiTestOptions = DEFAULT_GROUPED_MULTI_TEST_OPTIONS,
): ExerciseSelectionMode => {
  switch (form) {
    case 'TRUE_FALSE':
      return 'trueFalse';
    case 'MULTI_TEST':
      return 'multiSelect';
    case 'GROUPED_MULTI_TEST':
      return groupedMultiTestOptions.allowMultiplePerGroup ? 'groupedMultiSelect' : 'groupedSingleSelect';
    default:
      return 'single';
  }
};

export const isAnswerSelectionComplete = (
  mode: ExerciseSelectionMode,
  answers: ExerciseAnswer[],
  selectedAnswerIds: number[],
): boolean => {
  switch (mode) {
    case 'trueFalse':
      return isTrueFalseAnswerComplete(answers, selectedAnswerIds);
    case 'multiSelect':
      return isMultiSelectAnswerComplete(selectedAnswerIds);
    case 'groupedSingleSelect':
      return isTrueFalseAnswerComplete(answers, selectedAnswerIds);
    case 'groupedMultiSelect':
      return isGroupedMultiSelectAnswerComplete(answers, selectedAnswerIds);
    default:
      return selectedAnswerIds.length > 0;
  }
};

export const getChoicesSectionTitle = (phase: 'question' | 'result', mode: ExerciseSelectionMode): string => {
  switch (phase) {
    case 'result':
      return 'Odpowiedzi';
    case 'question':
      switch (mode) {
        case 'trueFalse':
          return 'Oceń każde stwierdzenie';
        case 'multiSelect':
          return 'Wybierz odpowiedzi';
        case 'groupedSingleSelect':
          return 'Wybierz odpowiedź w każdej grupie';
        case 'groupedMultiSelect':
          return 'Wybierz odpowiedzi w każdej grupie';
        default:
          return 'Wybierz odpowiedź';
      }
  }
};

export type TrueFalseAnswerGroup = {
  group: number;
  choices: ExerciseAnswer[];
};

/** Groups TRUE_FALSE choices by their `group` index (stable ascending order). */
export const groupTrueFalseAnswers = (answers: ExerciseAnswer[]): TrueFalseAnswerGroup[] => {
  const byGroup = new Map<number, ExerciseAnswer[]>();

  for (const answer of answers) {
    if (answer.group == null) {
      continue;
    }
    const existing = byGroup.get(answer.group) ?? [];
    existing.push(answer);
    byGroup.set(answer.group, existing);
  }

  return Array.from(byGroup.entries())
    .sort(([left], [right]) => left - right)
    .map(([group, choices]) => ({
      group,
      choices,
    }));
};

export const getSelectedAnswers = (answers: ExerciseAnswer[], selectedAnswerIds: number[]): ExerciseAnswer[] => {
  if (!selectedAnswerIds.length) {
    return [];
  }
  const selectedIds = new Set(selectedAnswerIds);
  return answers.filter(answer => selectedIds.has(answer.id));
};

export const isTrueFalseAnswerComplete = (answers: ExerciseAnswer[], selectedAnswerIds: number[]): boolean => {
  const groups = groupTrueFalseAnswers(answers);
  if (!groups.length) {
    return false;
  }

  const selectedByGroup = new Map<number, number>();
  for (const answerId of selectedAnswerIds) {
    const answer = answers.find(item => item.id === answerId);
    if (answer?.group == null) {
      continue;
    }
    selectedByGroup.set(answer.group, answerId);
  }

  return groups.every(({ group }) => selectedByGroup.has(group));
};

export const selectGroupedChoice = (
  selectedAnswerIds: number[],
  answerId: number,
  answers: ExerciseAnswer[],
): number[] => {
  const answer = answers.find(item => item.id === answerId);
  if (answer?.group == null) {
    return selectedAnswerIds;
  }

  const withoutGroup = selectedAnswerIds.filter(id => {
    const item = answers.find(entry => entry.id === id);
    return item?.group !== answer.group;
  });

  return [...withoutGroup, answerId];
};

export const toggleMultiSelectAnswer = (selectedAnswerIds: number[], answerId: number): number[] => {
  if (selectedAnswerIds.includes(answerId)) {
    return selectedAnswerIds.filter(id => id !== answerId);
  }
  return [...selectedAnswerIds, answerId];
};

export const isMultiSelectAnswerComplete = (selectedAnswerIds: number[]): boolean => selectedAnswerIds.length > 0;

export const isGroupedMultiSelectAnswerComplete = (answers: ExerciseAnswer[], selectedAnswerIds: number[]): boolean => {
  const groups = groupTrueFalseAnswers(answers);
  if (!groups.length) {
    return false;
  }

  const selectedByGroup = new Map<number, number>();
  for (const answerId of selectedAnswerIds) {
    const answer = answers.find(item => item.id === answerId);
    if (answer?.group == null) {
      continue;
    }
    selectedByGroup.set(answer.group, (selectedByGroup.get(answer.group) ?? 0) + 1);
  }

  return groups.every(({ group }) => (selectedByGroup.get(group) ?? 0) > 0);
};
